/**
 * AssistantMessageRouter — picks the v3 typed-parts renderer when
 * `formatting_v2` is on, otherwise the legacy AssistantMessage.
 *
 * This is the design's rollout integration point (PR 11): a single
 * component swap behind the PostHog flag. Production code that
 * imports `AssistantMessage` for chatV2 should switch to this
 * router; once formatting_v2 reaches 100% the router collapses to
 * `RenderedAssistantMessage` directly and the legacy import goes
 * away (PR 7 cleanup follow-up).
 *
 * Why a router rather than two parallel mount points: the chat
 * surface today imports `AssistantMessage` from many places
 * (history, streaming, retry, screenshot). Replacing them all with
 * a `formatting_v2 ? <V3 /> : <V2 />` ternary at every call site
 * is brittle. The router does it once.
 */
import { useApiFeatureFlag } from '@/hooks/useFeatureFlag'
import type { UIMessage, VersionInfo } from '../../types'
import { AssistantMessage } from './AssistantMessage'
import { RenderedAssistantMessage } from '../../render'
import type { UIMessagePart } from '@intuigence/response-parts'

/**
 * Drop-in superset of {@link AssistantMessage}'s props. The router
 * forwards the full prop bag to the legacy component on the
 * fall-through path, so swapping `AssistantMessage` →
 * `AssistantMessageRouter` at a call site is a one-line change.
 */
export interface AssistantMessageRouterProps {
  message: UIMessage
  conversationId?: string
  onFeedback?: (
    rating: number,
    feedbackMessage?: string,
    screenshot?: string,
    userQuestion?: string,
    assistantAnswer?: string,
  ) => void
  onRegenerate?: () => void
  onSendFollowUp?: (question: string) => void
  isStreaming?: boolean
  isLastMessage?: boolean
  onImageClick?: (imageUrl: string) => void
  agentNameOverride?: string
  agentAvatarUrlOverride?: string
  versionInfo?: VersionInfo | null
  onNavigateVersion?: (userMsgId: string, direction: 'prev' | 'next') => void
  userQuestion?: string
  /** FS-676: answer this message's egress-consent prompt (approve/deny card) */
  onConsentResponse?: (verdict: 'granted' | 'denied') => void
}

/**
 * The chatV2 UIMessage type already declares `parts: MessagePart[]`
 * for legacy types ('text' | 'reasoning' | 'file' | 'tool-call' |
 * 'tool-result' | 'source'). The v2 path persists a different shape
 * (UIMessagePart from @intuigence/response-parts) into the same
 * field at the DB column level. The narrow discriminator is the
 * presence of any `data-*` part type — those are exclusive to the
 * v2 vocabulary.
 *
 * Pure-text v2 messages (no data-* parts) render correctly through
 * the legacy renderer because the v2 backend already normalized the
 * text deltas server-side (PR 4); the legacy renderer's input is
 * clean and the safety nets are no-ops on it.
 */
function hasV2TypedParts(message: { parts?: ReadonlyArray<{ type: string }> }): UIMessagePart[] | null {
  const parts = message.parts
  if (!Array.isArray(parts)) return null
  const hasV2Marker = parts.some((p) => typeof p.type === 'string' && p.type.startsWith('data-'))
  if (!hasV2Marker) return null
  return parts as unknown as UIMessagePart[]
}

/**
 * Parts that route to the v3 renderer even when `formatting_v2` is OFF, because
 * the legacy renderer cannot display them at all and the alternative is the
 * user seeing nothing.
 *
 * FS-663 added `data-pending-share`: the answer text says "confirm below", so a
 * dropped card leaves the turn referring to a control that does not exist.
 */
const ALWAYS_ROUTED_PART_TYPES = new Set(['data-workflow-progress', 'data-pending-share'])

function hasAlwaysRoutedPart(message: { parts?: ReadonlyArray<{ type: string }> }): UIMessagePart[] | null {
  const parts = message.parts
  if (!Array.isArray(parts)) return null
  if (!parts.some((p) => ALWAYS_ROUTED_PART_TYPES.has(p.type))) return null
  return parts as unknown as UIMessagePart[]
}

export function AssistantMessageRouter(props: AssistantMessageRouterProps) {
  const { enabled, loading } = useApiFeatureFlag('formatting_v2', false)
  if (loading) {
    // Show legacy renderer while the flag resolves — returning null
    // blanked all assistant messages on first paint until PostHog fired.
    return <AssistantMessage {...props} />
  }
  const typedParts = enabled
    ? hasV2TypedParts(props.message as { parts?: ReadonlyArray<{ type: string }> })
    : hasAlwaysRoutedPart(props.message as { parts?: ReadonlyArray<{ type: string }> })
  if (typedParts) {
    return <RenderedAssistantMessage parts={typedParts} />
  }
  // Fall through to the legacy renderer when:
  //   - flag is off, OR
  //   - the message has no typed-vocabulary parts (legacy v1 row,
  //     pure-text v2 message, or in-flight stream).
  return <AssistantMessage {...props} />
}
